import {closeCurrentDialog, openDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useRef} from 'react';

import {head, isNil, isNotNil, mergeAll} from 'ramda';
import {isArray, isString} from 'ramda-adjunct';

import {
  PostSupplierOrderMaterialBasketItemRequest,
  useLazyGetArticleIdByManufacturerNumberAndManufacturerIdQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  DmsDataGrid,
  PriceListExternalFilters,
  PriceListRowData as PriceListDetailRowData,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  handleApiError,
  logError,
  refreshDmsDatagrid,
} from '@dms/shared';
import {ArticleCreate, ArticleCreateDefaultValues, PriceListDetail} from '@dms/shared-aftersales';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {TestIdProps, useLatestValue} from 'shared/web';

import type {ActionCallback, ExternalFilters, QueryFilterObject} from 'features/eag-grid';

import {DEFAULT_TO_ORDER_QUANTITY} from '../constants/defaultToOrderQuantity';
import {PriceListRowData} from '../types/PriceListRowData';

const SUPPLIER_ORDER_MATERIAL_EXTERNAL_FILTER_ID = 'supplier-order-material-external-filter';

const PREDEFINED_COLUMNS = [
  'name',
  'catalogNumber',
  'orderingNumber',
  'manufacturerId',
  'supplierId',
  'purchasePrice',
  'recommendedPrice',
  'supplierDiscountGroup',
  'supplierOrderingNumber',
  'supplierManufacturerName',
  'supplierManufacturerCode',
  'standardOrderQuantity',
  'localStock',
  'totalStock',
] as const;

interface MaterialPriceListProps extends TestIdProps {
  warehouseId: string;
  branchId: string;
  authorizationProfileId: string;
  onAddMaterialToBasket: (
    article: PostSupplierOrderMaterialBasketItemRequest['body']['article']
  ) => Promise<void>;
  hasPendingRequest: boolean;
}

export function MaterialPriceListTab(props: MaterialPriceListProps) {
  const externalFilters = useRef<ArticleCreateDefaultValues>({});
  const hasPendingRequestRef = useLatestValue(props.hasPendingRequest);
  const [getArticleIdByManufacturerNumberAndManufacturerId] =
    useLazyGetArticleIdByManufacturerNumberAndManufacturerIdQuery();

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([
      filter,
      {branchId: props.branchId, authorizationProfileId: props.authorizationProfileId},
    ]);

  const handleNewArticle = (rowData: PriceListRowData) => {
    openDialog(
      <ArticleCreate
        defaultValues={{
          catalogNumber: rowData.catalogNumber,
          manufacturerId: rowData.manufacturerId,
          warehouseId: props.warehouseId,
          name: rowData.name,
        }}
        onArticleCreate={(createdArticleData) => {
          props.onAddMaterialToBasket([
            {articleId: createdArticleData.articleId, toOrderQuantity: DEFAULT_TO_ORDER_QUANTITY},
          ]);
          closeCurrentDialog();
        }}
        onAddToBasket={(articleId) => {
          props.onAddMaterialToBasket([{articleId, toOrderQuantity: DEFAULT_TO_ORDER_QUANTITY}]);
          closeCurrentDialog();
        }}
        hasAddToBasketButton
        submitButtonTitle={i18n.t('general.actions.createAndAdd')}
        data-testid={testIds.warehouse.articleList('createNewArticle')}
      />,
      {
        id: 'createNewArticleDialog',
        title: i18n.t('general.labels.createNew'),
        scrollBehavior: 'outside',
        withAdditionalFooter: true,
      }
    );
  };

  const handleAddMaterialToBasket = async (rowData: PriceListRowData) => {
    const articleIdResponse = await getArticleIdByManufacturerNumberAndManufacturerId({
      warehouseId: props.warehouseId,
      manufacturerNumber: rowData.catalogNumber,
      manufacturerId: rowData.manufacturerId,
    })
      .unwrap()
      .catch(handleApiError);

    const articleId = articleIdResponse?.articleId;

    if (isNotNil(articleId)) {
      props
        .onAddMaterialToBasket([{articleId, toOrderQuantity: DEFAULT_TO_ORDER_QUANTITY}])
        .then(() => refreshDmsDatagrid(WAREHOUSE_GRID_CODES.priceListBasket, {deselectAll: true}));
      return;
    }

    handleNewArticle({
      catalogNumber: rowData.catalogNumber,
      manufacturerId: rowData.manufacturerId,
      name: rowData.name,
    });
  };

  const warehouseActionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = async ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    if (hasPendingRequestRef.current) {
      return showNotification.warning(
        i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction')
      );
    }

    const priceListId = isArray(rowId) ? head(rowId) : rowId;
    const itemManufacturerNumber = getColumnData<string>('catalogNumber')?.value;
    const itemManufacturerId = getColumnData<{id: string}>('manufacturerId')?.value?.id;
    const itemName = getColumnData<string>('name')?.value;

    if (isNil(itemManufacturerNumber)) {
      return logError('ItemManufacturerNumber is not defined');
    }

    if (isNil(itemManufacturerId)) {
      return logError('ItemManufacturerId is not defined');
    }

    if (isNil(itemName)) {
      return logError('ItemName is not defined');
    }

    if (isNil(priceListId)) {
      return logError('priceListId was not defined');
    }

    const priceListRowData = {
      name: {value: getColumnData('name')?.value},
      catalogNumber: {value: getColumnData('catalogNumber')?.value},
      supplierOrderingNumber: {value: getColumnData('supplierOrderingNumber')?.value},
      manufacturerId: {value: getColumnData('manufacturerId')?.value},
      supplierId: {value: getColumnData('supplierId')?.value},
      purchasePrice: {value: getColumnData('purchasePrice')?.value},
      recommendedPrice: {value: getColumnData('recommendedPrice')?.value},
      supplierDiscountGroup: getColumnData('supplierDiscountGroup')?.value,
      supplierManufacturerName: getColumnData('supplierManufacturerName')?.value,
      supplierManufacturerCode: getColumnData('supplierManufacturerCode')?.value,
      standardOrderQuantity: getColumnData('standardOrderQuantity')?.value,
      localStock: getColumnData('localStock')?.value,
      totalStock: getColumnData('totalStock')?.value,
    } as PriceListDetailRowData;

    await match(actionKey)
      .with('redirectDetail', () => {
        openDialog(
          <PriceListDetail
            id={priceListId}
            priceListRowData={priceListRowData}
            data-testid={testIds.warehouse.priceList('dialogs.detail')}
          />,
          {
            id: 'detailDialog',
            title: priceListRowData?.name?.value,
            size: 'large',
            withAdditionalFooter: true,
          }
        );
      })
      .with('addToBasket', async () => {
        await handleAddMaterialToBasket({
          catalogNumber: itemManufacturerNumber,
          manufacturerId: itemManufacturerId,
          name: itemName,
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const handleExternalFiltersChange = (filters: ExternalFilters) => {
    const articleCreateDefaultValues: ArticleCreateDefaultValues = {};

    if (isString(filters.catalogNumber)) {
      articleCreateDefaultValues.catalogNumber = filters.catalogNumber;
    }

    if (isString(filters.manufacturer)) {
      articleCreateDefaultValues.manufacturerId = filters.manufacturer;
    }

    externalFilters.current = articleCreateDefaultValues;
  };

  return (
    <FullscreenDataGridWrapper
      externalFilterId={SUPPLIER_ORDER_MATERIAL_EXTERNAL_FILTER_ID}
      hideCardWrapper
      data-testid={props['data-testid']}
    >
      <DmsDataGrid
        gridCode={WAREHOUSE_GRID_CODES.priceListBasket}
        actionCallback={warehouseActionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        externalFilterId={SUPPLIER_ORDER_MATERIAL_EXTERNAL_FILTER_ID}
        _useAsLastResort_definitionBehaviorOverrides={{filterMode: 'NATIVE'}}
        data-testid={WAREHOUSE_GRID_CODES.priceListBasket}
        filterComponent={PriceListExternalFilters}
        onExternalFilterChange={handleExternalFiltersChange}
        queryModifier={queryModifier}
      />
    </FullscreenDataGridWrapper>
  );
}
